import React, { ReactNode } from 'react'
import { Form } from 'react-bootstrap'
import { Link } from 'widgets-for-react'
import { Img, ImgProps } from '../components/img'
import { cn } from '../utils/addToClassName'
import { Clickable, ClickableProps } from '../utils/clickable'
import { translate } from '../utils/translate'
import { FormNotification, FormNotificationProps } from './formControls'
import { FormUtils } from './formUtils'

// ==========================================================================
// CONFIG
// ==========================================================================

export const formSkeletonConfig: FormSkeletonConfigProps = {
  sloganClassName: '',
  logoWidth: 150,
  logoHeight: 57,
  preferredColorScheme: 'dark',
  colorSchemeConfig: {
    light: {
      logoSrc: 'https://giustoragev2.blob.core.windows.net/shoppinger/logo-hds.svg',
      image: {
        alt: 'Fjords - light',
        src: 'https://giustoragev2.blob.core.windows.net/shoppinger/fjords-512x384.jpeg',
        crops: [
          { width: 512, url: 'https://giustoragev2.blob.core.windows.net/shoppinger/fjords-512x384.jpeg' },
          { width: 1024, url: 'https://giustoragev2.blob.core.windows.net/shoppinger/fjords-1024x768.jpeg' },
          { width: 2048, url: 'https://giustoragev2.blob.core.windows.net/shoppinger/fjords-2048x1536.jpeg' },
        ],
      },
    },
    dark: {
      logoSrc: 'https://giustoragev2.blob.core.windows.net/shoppinger/logo-hds-white.svg',
      image: {
        alt: 'Northern lights - dark',
        src: 'https://giustoragev2.blob.core.windows.net/shoppinger/northern-lights-512x384.jpeg',
        crops: [
          { width: 512, url: 'https://giustoragev2.blob.core.windows.net/shoppinger/northern-lights-512x384.jpeg' },
          { width: 1024, url: 'https://giustoragev2.blob.core.windows.net/shoppinger/northern-lights-1024x768.jpeg' },
          { width: 2048, url: 'https://giustoragev2.blob.core.windows.net/shoppinger/northern-lights-2048x1536.jpeg' },
        ],
      },
    },
  },
}

// ==========================================================================
// FORM SKELETON
// ==========================================================================

type FormSkeletonConfigProps = {
  logoWidth: number
  logoHeight: number
  sloganClassName?: string
  preferredColorScheme?: FormSkeletonColorModes | 'none'
  colorSchemeConfig: {
    [key in FormSkeletonColorModes]: FormSkeletonColorSchemeProps
  }
}

type FormSkeletonColorSchemeProps = {
  logoSrc: string
  image: Pick<ImgProps, 'alt' | 'src' | 'crops'>
}

type FormSkeletonColorModes = 'light' | 'dark'

// ----- STRUCTURE

export type FormTypes =
  | 'sign-up'
  | 'sign-up-succes'
  | 'login'
  | 'login-admin'
  | 'reset-password-request'
  | 'reset-password-request-success'
  | 'reset-password'
  | 'reset-password-success'
  | 'resend-activation'
  | 'resend-activation-success'
  | 'trial-to-pro'
  | 'trial-to-pro-success'
  | 'trial-to-pro-failed'
  | 'redirect-to-psp'
  | 'activate-account'
  | 'activate-account-success'
  | 'activate-account-failed'
  | 'activate-change-password'
  | 'student-subscription'
  | 'change-password'
  | 'change-password-success'

export type FormSkeletonProps = {
  type: FormTypes
  colorScheme?: FormSkeletonColorModes
  children: ReactNode
  onSubmit?: (e: React.FormEvent<HTMLFormElement>) => void
  rootUrl: string
} & FormSkeletonInnerProps &
  (
    | {
        layout: 'text'
        header?: {
          slogan?: string
        }
      }
    | {
        layout: 'text-image'
        header?: {
          slogan?: string
          image?: Pick<ImgProps, 'alt' | 'src' | 'crops'>
        }
      }
  )

export const FormSkeletonLayout = (props: FormSkeletonProps) => {
  const { children, colorScheme, layout, onSubmit, header, type, rootUrl } = props
  const { colorSchemeConfig, logoHeight, logoWidth, preferredColorScheme, sloganClassName } = formSkeletonConfig
  const usesDarkMode = colorScheme
    ? colorScheme == 'dark' || preferredColorScheme == 'dark'
    : preferredColorScheme == 'dark'
  return (
    <div className={cn(`auth auth--${type} auth--${layout}`, usesDarkMode ? ' auth--clr-inverted' : '')}>
      {type != 'login-admin' && (
        <header className="auth__header">
          <div className="auth__header-inner">
            <Link to={rootUrl} className="auth__logo" title={translate('a11y:navigateHome')}>
              <span className="sr-text">{translate('a11y:navigateHome')}</span>
              <img
                src={usesDarkMode ? colorSchemeConfig['dark'].logoSrc : colorSchemeConfig['light'].logoSrc}
                alt=""
                width={logoWidth}
                height={logoHeight}
              />
            </Link>

            {header?.slogan && <p className={cn('auth__slogan', sloganClassName)}>{header.slogan}</p>}
          </div>

          {props.layout == 'text-image' &&
            (usesDarkMode ? (
              <FormSkeletonVisual {...(props.header?.image ?? colorSchemeConfig['dark'].image)} />
            ) : (
              <FormSkeletonVisual {...(props.header?.image ?? colorSchemeConfig['light'].image)} />
            ))}
        </header>
      )}

      <main className="auth__main form-skeleton fs">
        {onSubmit !== undefined ? (
          <FormSkeletonForm onSubmit={(e: React.FormEvent<HTMLFormElement>) => onSubmit(e)}>
            <FormSkeletonInner {...props}>{children}</FormSkeletonInner>
          </FormSkeletonForm>
        ) : (
          <div className="auth__form fs__form">
            <FormSkeletonInner {...props}>{children}</FormSkeletonInner>
          </div>
        )}
      </main>
    </div>
  )
}

const FormSkeletonVisual = (props: Pick<ImgProps, 'alt' | 'src' | 'crops'>) => (
  <div className="auth__visual">
    <Img
      className="auth__image"
      width="50vw"
      height="100vh"
      sizes="(min-width: 60rem) 50vw, 100vw"
      loading="eager"
      {...props}
    />
  </div>
)

export const FormSkeletonForm = (p: {
  onSubmit: (e: React.FormEvent<HTMLFormElement>) => void
  children: ReactNode
}) => (
  <Form noValidate onSubmit={p.onSubmit} className="auth__form fs__form">
    {p.children}
  </Form>
)

type FormSkeletonInnerProps = {
  title: string
  intro?: React.ReactNode
  notification?: FormNotificationProps
  children: React.ReactNode
  primaryAction?: ClickableProps & { showLoader?: boolean }
  secondaryAction?: ClickableProps
  footer?: React.ReactNode
}

const FormSkeletonInner = (props: FormSkeletonInnerProps) => {
  const { title, intro, children, notification, primaryAction, secondaryAction, footer } = props
  return (
    <>
      <header className="fs__header">
        <h1 className="fs__title">{title}</h1>
        {intro &&
          (typeof intro == 'string' ? (
            <div className="fs__intro" dangerouslySetInnerHTML={{ __html: intro }} />
          ) : (
            <p className="fs__intro text--styled">{intro}</p>
          ))}
      </header>

      {notification && <FormNotification {...notification} />}

      <div className="fs__body">{children}</div>

      {(primaryAction || secondaryAction) && (
        <div className="fs__clickables ls-clickables">
          {primaryAction && (
            <div className="ls-clickable">
              <Clickable
                {...primaryAction}
                styleKind="button"
                className="fs__clickable button button--primary"
                label={
                  primaryAction.showLoader && typeof primaryAction.label == 'string' ? (
                    <FormUtils.SubmittingSpinnerOrText
                      showLoader={primaryAction.showLoader || false}
                      text={primaryAction.label}
                    />
                  ) : (
                    primaryAction.label
                  )
                }
              />
            </div>
          )}
          {secondaryAction && (
            <div className="ls-clickable">
              <Clickable {...secondaryAction} styleKind="button" className="fs__clickable button button--secondary" />
            </div>
          )}
        </div>
      )}

      {footer && <footer className="fs__footer text--styled">{footer}</footer>}
    </>
  )
}
